import { cva } from "class-variance-authority";

/* The Sirdab button's recipe, in a module of its own with NO "use client"
   directive: button.tsx is a client component (its loading guard attaches
   a handler), which turns everything it exports into a client reference,
   and a client reference cannot be CALLED from a server component. The
   recipe is a pure function, so it lives here and a server page may
   style a link with it directly:

       <Link href="/" className={buttonVariants({ variant: "secondary", size: "xl" })}>

   That is the sanctioned way to render a LINK as a button from a server
   tree. Button asChild around a server component's Link is not: the child
   crosses the RSC boundary as a Flight lazy that Radix Slot cannot slot,
   and the page fails with "Slot failed to slot onto its children".
   Client components that own their Link keep asChild. The doctrine for
   each variant and size is in button.tsx's header. */
const buttonVariants = cva(
  // Leading icons pull toward the start edge (a −3px optical pull,
  // taken at the nearest on-grid step); the 6px gap spaces icon from text.
  // Real disabled buttons KEEP pointer events (cursor feedback); aria-disabled
  // asChild elements drop them. [&:disabled] is deliberate: the redefined
  // `disabled:` variant also matches aria-disabled and would contradict the
  // very next class.
  "relative inline-flex shrink-0 items-center justify-center gap-1.5 text-label whitespace-nowrap transition-colors duration-75 select-none [&:disabled]:pointer-events-auto aria-disabled:pointer-events-none data-[loading=true]:text-transparent data-[loading=true]:[&>svg]:opacity-0 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&>svg:first-child]:not-only:-ms-0.5",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-none hover:bg-primary-hover active:bg-primary-active data-[state=on]:bg-primary-active data-[state=open]:bg-primary-active disabled:bg-btn-disabled-surface disabled:text-btn-disabled-text",
        destructive:
          "border border-btn-destructive bg-background/20 text-btn-destructive hover:bg-btn-destructive/10 active:bg-btn-destructive/20 disabled:border-btn-destructive-border-disabled disabled:bg-background/20 disabled:text-btn-destructive-text-disabled",
        secondary:
          "border border-btn-border bg-background text-foreground hover:border-btn-border-hover hover:bg-btn-surface-hover active:border-btn-border-hover active:bg-btn-surface-active data-[state=open]:border-btn-border-hover data-[state=open]:bg-btn-surface-hover data-[state=on]:border-btn-border-hover data-[state=on]:bg-btn-surface-hover disabled:bg-background disabled:text-subtle disabled:[&_svg]:icon-quiet disabled:border-btn-border",
        outline:
          "border border-btn-border bg-transparent text-foreground hover:border-btn-border-hover hover:bg-btn-surface-hover active:border-btn-border-hover active:bg-btn-surface-active data-[state=open]:border-btn-border-hover data-[state=open]:bg-btn-surface-hover data-[state=on]:border-btn-border-hover data-[state=on]:bg-btn-surface-hover disabled:bg-transparent disabled:text-subtle disabled:[&_svg]:icon-quiet disabled:border-btn-border",
        ghost:
          "text-foreground hover:bg-btn-ghost-surface-hover active:bg-btn-ghost-surface-active data-[state=open]:bg-btn-ghost-surface-active disabled:text-subtle disabled:[&_svg]:icon-quiet",
        chip: "border border-btn-border bg-transparent text-muted-foreground [&_svg]:icon-quiet hover:[&_svg]:icon-quiet-hover hover:bg-btn-ghost-surface-hover active:bg-btn-ghost-surface-active data-[state=on]:bg-btn-ghost-surface-active data-[state=open]:bg-btn-ghost-surface-active disabled:bg-transparent disabled:text-subtle disabled:[&_svg]:icon-quiet",
        link: "text-foreground underline-offset-4 hover:underline disabled:text-subtle disabled:[&_svg]:icon-quiet",
      },
      /* Icons are half the control height. Labeled sizes scale only the
               LEADING icon (first-child) so trailing chevrons hold at 16px;
               icon-only squares scale their lone icon. The 16px base in the
               root covers default (32px) and all trailing icons. */
      size: {
        default: "h-control rounded-lg px-2.5",
        xs: "h-control-xs gap-1 rounded-md px-1.5 text-caption [&_svg:not([class*='size-'])]:size-3",
        sm: "h-control-sm rounded-lg px-2 [&>svg:first-child:not([class*='size-'])]:size-3.5",
        lg: "h-control-lg rounded-lg px-3 text-body [&>svg:first-child:not([class*='size-'])]:size-4.5",
        xl: "h-control-xl rounded-xl px-3 text-body [&>svg:first-child:not([class*='size-'])]:size-5",
        touch:
          "h-control-touch rounded-xl px-3.5 text-body [&>svg:first-child:not([class*='size-'])]:size-5.5",
        // px-0 is load-bearing: the bridge's density rule pads every
        // button 12px inline; without an explicit padding utility the
        // icon squares grow to fit it and stop being square.
        icon: "aspect-square h-control rounded-lg px-0",
        "icon-xs":
          "aspect-square h-control-xs rounded-md px-0 [&_svg:not([class*='size-'])]:size-3",
        "icon-sm":
          "aspect-square h-control-sm rounded-lg px-0 [&_svg:not([class*='size-'])]:size-3.5",
        "icon-lg":
          "aspect-square h-control-lg rounded-lg px-0 [&_svg:not([class*='size-'])]:size-4.5",
        "icon-xl":
          "aspect-square h-control-xl rounded-xl px-0 [&_svg:not([class*='size-'])]:size-5",
        "icon-touch":
          "aspect-square h-control-touch rounded-xl px-0 [&_svg:not([class*='size-'])]:size-5.5",
      },
      /* Radius stays a PROP, never a call-site override. `circle` is
               for IDENTITY triggers only: the avatar that opens the account
               menu, where the round shape is the thing itself and the fill
               steps on hover instead of chrome appearing around it. Ordinary
               actions keep the house radii, including composer actions: a
               circular send/dictate reads as a foreign control next to our
               buttons. */
      shape: {
        default: "",
        circle: "rounded-full",
      },
    },
    compoundVariants: [
      // A link is text, not a box, whatever size it inherits.
      { variant: "link", class: "h-auto rounded-none p-0" },
    ],
    defaultVariants: {
      variant: "default",
      size: "default",
      shape: "default",
    },
  },
);

export { buttonVariants };
